import { useState, type FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotEl = form.querySelector('[name="phone_alt"]') as HTMLInputElement;
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      setFormMessage('提交成功，我们会尽快与您联系！');
      return;
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d9qbpsvnahb7382ov9cg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = serverMsg.includes('spam') || serverMsg.includes('form data is spam');

      if (res.ok && parsed?.code === 'OK' && !isSpam) {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快与您联系！');
        form.reset();
      } else {
        setFormStatus('error');
        setFormMessage(serverMsg || '提交失败，请稍后重试或直接拨打客服热线。');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试或直接拨打客服热线 400-000-0000。');
    }
  };

  const schemaData = [
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '关于东升大舞台',
      description: '东升大舞台品牌介绍、口碑声誉与联系方式，了解我们的使命与核心价值。',
      url: 'https://dongshengdawutai.com/about',
      isPartOf: {
        '@type': 'WebSite',
        name: '东升大舞台',
        url: 'https://dongshengdawutai.com/',
      },
    },
  ];

  const reputations = [
    {
      icon: 'ri-star-line',
      value: '500万+',
      label: '累计用户',
      description: '东升大舞台自上线以来，已累计服务超过500万演艺娱乐爱好者',
    },
    {
      icon: 'ri-thumb-up-line',
      value: '4.8/5',
      label: '用户评分',
      description: '在各大应用商店获得4.8分的高分好评，用户满意度持续领先',
    },
    {
      icon: 'ri-award-line',
      value: '12项',
      label: '行业荣誉',
      description: '荣获多项演艺娱乐行业大奖，被评选为年度最佳文化娱乐应用',
    },
    {
      icon: 'ri-team-line',
      value: '200+',
      label: '合作达人',
      description: '与超过200位优秀演艺达人达成深度合作，持续输出优质内容',
    },
  ];

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="关于我们 - 东升大舞台 | 品牌介绍与联系方式"
        description="了解东升大舞台的品牌故事、核心使命与口碑声誉。我们致力于为用户提供最优质的演艺娱乐内容和互动体验。提供官方联系方式与客服渠道。"
        keywords="东升大舞台,关于东升大舞台,东升大舞台品牌,东升大舞台联系方式"
        canonicalUrl="https://dongshengdawutai.com/about"
        schemaData={schemaData}
      />
      <Navbar />
      <main className="pt-20 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>

          {/* Brand Introduction */}
          <section className="mb-14 md:mb-20">
            <div className="max-w-3xl">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-6">
                关于东升大舞台
              </h1>
              <div className="space-y-4 text-foreground-700 leading-relaxed text-sm md:text-base">
                <p>
                  <strong>东升大舞台</strong>成立于2020年，是一家专注于演艺娱乐内容的互联网平台。我们致力于将中国优秀的传统与现代演艺文化通过数字化方式呈现给更广泛的观众群体，让每一个人都能随时随地享受精彩纷呈的舞台艺术。
                </p>
                <p>
                  平台汇集了歌舞、戏曲、相声、魔术、杂技、音乐演奏等多元表演艺术门类，与全国超过200位优秀演艺达人建立了深度合作关系。无论是经典的传统剧目还是新锐的现代演出，在东升大舞台都能找到。
                </p>
                <p>
                  我们秉持"让演艺触手可及"的使命，通过高清直播、点播回放、互动体验等功能，打破了地域和时间的限制，让演艺文化的魅力传遍千家万户。东升大舞台不仅是一个观看平台，更是一个连接演艺人与观众的互动社区。
                </p>
                <p>
                  未来，东升大舞台将继续深耕演艺娱乐领域，不断优化产品体验，拓展内容生态，致力于成为中国最具影响力的演艺文化数字化平台。
                </p>
              </div>
            </div>
          </section>

          {/* Reputation Section */}
          <section className="mb-14 md:mb-20">
            <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-8 text-center">
              口碑与荣誉
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {reputations.map((item, index) => (
                <div
                  key={index}
                  className="bg-background-100 border border-background-200/70 rounded-lg p-5 md:p-6 text-center"
                >
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-full bg-accent-100">
                    <i className={`${item.icon} text-xl text-accent-600`}></i>
                  </div>
                  <div className="font-heading text-2xl md:text-3xl font-bold text-accent-600 mb-1">
                    {item.value}
                  </div>
                  <div className="text-sm font-semibold text-foreground-900 mb-2">
                    {item.label}
                  </div>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Section */}
          <section className="mb-14 md:mb-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
              <div>
                <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-4">
                  联系我们
                </h2>
                <p className="text-foreground-600 text-sm md:text-base mb-8 leading-relaxed">
                  如果您有任何问题、建议或合作意向，欢迎通过以下方式与我们取得联系。我们的客服团队将在工作时间内尽快回复您。
                </p>

                <div className="flex flex-col gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
                      <p className="text-sm text-foreground-600">kefu@dongshengdawutai.com</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                      <i className="ri-phone-line text-primary-600"></i>
                    </div>
                    <div>
                      <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">客服热线</h4>
                      <p className="text-sm text-foreground-600">400-000-0000</p>
                      <p className="text-xs text-foreground-500 mt-0.5">工作日 9:00 - 18:00</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                      <i className="ri-map-pin-line text-primary-600"></i>
                    </div>
                    <div>
                      <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">公司地址</h4>
                      <p className="text-sm text-foreground-600">北京市朝阳区东升科技园A座12层</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="bg-background-100 border border-background-200/70 rounded-lg p-6 md:p-8">
                  <h3 className="font-heading text-lg font-bold text-foreground-950 mb-6">
                    在线留言
                  </h3>
                  <form
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-4"
                  >
                    <div>
                      <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="about-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-300/60 rounded-md text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>

                    <div>
                      <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="about-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-300/60 rounded-md text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的电子邮箱"
                      />
                    </div>

                    <div>
                      <label htmlFor="about-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        主题
                      </label>
                      <input
                        id="about-subject"
                        name="subject"
                        type="text"
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-300/60 rounded-md text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请选择或输入留言主题"
                      />
                    </div>

                    <div>
                      <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="about-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-300/60 rounded-md text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言内容（最多500字）"
                      ></textarea>
                    </div>

                    <div className="contact-form-honeypot">
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>

                    <button
                      type="submit"
                      className="inline-flex items-center justify-center px-6 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
                    >
                      <i className="ri-send-plane-line mr-2"></i>
                      提交留言
                    </button>

                    {formStatus === 'success' && (
                      <p className="text-sm text-green-600 bg-green-50 px-4 py-2.5 rounded-md">
                        <i className="ri-check-line mr-1"></i>
                        {formMessage}
                      </p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-600 bg-red-50 px-4 py-2.5 rounded-md">
                        <i className="ri-error-warning-line mr-1"></i>
                        {formMessage}
                      </p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}